/**
 * The sealed frame: a web page Gyro did not write (a page it generated, a file a person attached) runs in it with its own
 * scripts, and can load scripts, styles, fonts, pictures and media from https, but can never send anything out, never reach
 * the person's files or Gyro, and never navigate away (owner, 2026-10-22: "Load, never send"). The design and its residual
 * risk are docs/DESIGN-html-preview-panel.md; this module is the part every host shares: the frame page, its policy and the
 * two messages of its handshake.
 *
 * Shape. Gyro's page (the desktop shell, a host app's page) holds an iframe, sandboxed with `allow-scripts` alone, whose
 * address is the frame page on an origin of its own: `https://gyro-sealed.desktop/frame.html` in the desktop app (a WebView2
 * virtual host), `<gyro-svc>/v1/sealed-frame` on the web (served with the policy below as a header too). The frame page posts
 * READY to its parent; the parent answers once with RENDER and the page's HTML; the frame page takes that one message from
 * its parent (a listed origin) and shows the HTML in a nested iframe of its own, again sandboxed with `allow-scripts` alone,
 * whose `srcdoc` document inherits the frame page's policy. The parent acts on nothing else the frame sends.
 *
 * Why nested: the page's own scripts then live two windows below Gyro and one below a frame page they cannot touch (two
 * opaque origins). They cannot replay READY from the frame Gyro listens to, and every navigation they start is a navigation
 * of the nested frame, which the frame page's `frame-src 'none'` refuses before any request leaves (Chromium checks a
 * frame's navigations against its parent's frame-src). Proven on the fleet before the build (2026-10-22): the variant that
 * wrote the page over the frame page with document.write leaked a `location=` navigation and let the page replay READY.
 */
/** The frame page's policy, as a <meta> in the page itself and (with SEALED_FRAME_HEADER_EXTRA) as a response header. */
export const SEALED_FRAME_POLICY = [
    "default-src 'none'",
    // 'unsafe-eval' is kept on purpose: libraries pages load from a CDN compile templates with it (Alpine.js, Vue's full
    // build, Handlebars). It gives a page no power its inline scripts do not already have inside this policy.
    "script-src 'unsafe-inline' 'unsafe-eval' https:",
    "style-src 'unsafe-inline' https:",
    'img-src data: blob: https:',
    'font-src data: https:',
    'media-src data: blob: https:',
    "connect-src 'none'",
    "form-action 'none'",
    "frame-src 'none'",
    "worker-src 'none'",
    "base-uri 'none'",
].join('; ');
/**
 * What only a header can carry, added after SEALED_FRAME_POLICY on the web route: the frame page is sandboxed even when
 * something frames it without the attribute or opens it on its own (an opaque origin, scripts only), and only listed
 * origins may frame it.
 */
export function sealedFrameHeaderPolicy(frameAncestors) {
    const ancestors = ["'self'", ...frameAncestors.filter((origin) => SEALED_ORIGIN.test(origin))];
    return `${SEALED_FRAME_POLICY}; sandbox allow-scripts; frame-ancestors ${[...new Set(ancestors)].join(' ')}`;
}
/** The iframe's sandbox: scripts only. No same origin, forms, popups, top navigation, modals or downloads. */
export const SEALED_FRAME_SANDBOX = 'allow-scripts';
/** The frame page tells its parent it is listening. */
export const SEALED_FRAME_READY = 'gyro-sealed-frame:ready';
/** The parent's one answer: `{ type: SEALED_FRAME_RENDER, html }`. */
export const SEALED_FRAME_RENDER = 'gyro-sealed-frame:render';
/** The web route that serves the frame page (gyro-svc, and through a host's proxy). */
export const SEALED_FRAME_PATH = '/v1/sealed-frame';
/** The desktop app's frame page: a WebView2 virtual host of its own, mapped to a folder holding this page alone. */
export const DESKTOP_SEALED_FRAME_HOST = 'gyro-sealed.desktop';
export const DESKTOP_SEALED_FRAME_URL = `https://${DESKTOP_SEALED_FRAME_HOST}/frame.html`;
/** The desktop shell's origin, the only parent the desktop frame page answers. */
export const DESKTOP_SHELL_ORIGIN = 'https://gyro.desktop';
/** A web origin: scheme, host and optional port, nothing else. */
const SEALED_ORIGIN = /^https?:\/\/[a-z0-9.-]+(?::\d{1,5})?$/i;
/**
 * The script placed at the very start of a page shown in the sealed frame (after its doctype). A sandboxed page has no
 * storage of its own (its origin is opaque), and a page that touches localStorage or sessionStorage would stop at the first
 * call; these keep what the page stores in memory, for as long as the preview shows it.
 */
const STORAGE_SHIM = '<script>(function(){function m(){var s=new Map();return{get length(){return s.size},key:function(i){var k=Array.from(s.keys())[i];return k===undefined?null:k},getItem:function(k){k=String(k);return s.has(k)?s.get(k):null},setItem:function(k,v){s.set(String(k),String(v))},removeItem:function(k){s.delete(String(k))},clear:function(){s.clear()}}}["localStorage","sessionStorage"].forEach(function(n){try{Object.defineProperty(window,n,{value:m(),configurable:true,enumerable:true})}catch(e){}})})();</script>';
/** JSON for inside a <script>: no "</" or line separators that could end the element or the statement. */
function scriptJson(value) {
    return JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}
/** Text for an HTML attribute value in double quotes. */
function attribute(value) {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
/**
 * The frame page. It does nothing unless it is in a sealed frame (a parent window, and an opaque origin, which only a
 * sandbox without allow-same-origin gives). It posts READY once loaded, takes one RENDER from its parent if that parent is
 * on a listed origin, and shows that HTML in a nested sealed frame. When the nested page navigates (a refused navigation
 * shows the browser's error page, which loads again), the frame page starts the page again and says so; a page that keeps
 * doing it is stopped.
 */
export function sealedFrameDocument(options) {
    const parents = [...new Set(options.parents.filter((origin) => SEALED_ORIGIN.test(origin)))];
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${attribute(SEALED_FRAME_POLICY)}">
<meta name="referrer" content="no-referrer">
<meta name="color-scheme" content="light">
<title>Gyro sealed frame</title>
<style>
html, body { margin: 0; height: 100%; background: #fff; }
#page { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
#note { position: absolute; left: 8px; right: 8px; bottom: 8px; z-index: 1; margin: 0; padding: 8px 12px; border-radius: 8px; background: #26262b; color: #fff; font: 13px/1.4 "Segoe UI", system-ui, sans-serif; }
#note[hidden] { display: none; }
</style>
<script>
(function () {
  "use strict";
  var parents = ${scriptJson(parents)};
  var sameOriginParent = ${options.sameOriginParent ? 'true' : 'false'};
  var READY = ${scriptJson(SEALED_FRAME_READY)};
  var RENDER = ${scriptJson(SEALED_FRAME_RENDER)};
  var SHIM = ${scriptJson(STORAGE_SHIM)};
  if (window.parent === window || String(self.origin) !== "null") return;
  if (sameOriginParent) parents.push(location.origin);
  var taken = false;
  var restarts = [];
  function note(text) {
    var element = document.getElementById("note");
    if (!element) return;
    element.textContent = text;
    element.hidden = !text;
  }
  function withShim(html) {
    var doctype = /^\\uFEFF?\\s*<!doctype[^>]*>/i.exec(html);
    return doctype ? doctype[0] + SHIM + html.slice(doctype[0].length) : SHIM + html;
  }
  function show(html) {
    var frame = document.createElement("iframe");
    frame.id = "page";
    frame.title = "Web page";
    frame.setAttribute("sandbox", "allow-scripts");
    frame.setAttribute("referrerpolicy", "no-referrer");
    frame.setAttribute("credentialless", "");
    var loads = 0;
    frame.addEventListener("load", function () {
      loads += 1;
      if (loads === 1) return;
      frame.remove();
      var now = Date.now();
      restarts = restarts.filter(function (at) { return now - at < 10000; });
      restarts.push(now);
      if (restarts.length > 3) { note("The page kept trying to open another address, so Gyro stopped it."); return; }
      note("The page tried to open another address, so Gyro started it again.");
      show(html);
    });
    frame.srcdoc = withShim(html);
    document.body.appendChild(frame);
  }
  function receive(event) {
    if (taken || event.source !== window.parent || parents.indexOf(event.origin) < 0) return;
    var data = event.data;
    if (!data || typeof data !== "object" || data.type !== RENDER || typeof data.html !== "string") return;
    taken = true;
    window.removeEventListener("message", receive);
    show(data.html);
  }
  window.addEventListener("message", receive);
  function ready() { window.parent.postMessage({ type: READY }, "*"); }
  if (document.readyState === "complete") ready(); else window.addEventListener("load", ready);
})();
</script>
</head>
<body><p id="note" role="status" hidden></p></body>
</html>
`;
}
/** The desktop app's frame page (desktop/src/Gyro.Agent/SealedFrame/frame.html is this, byte for byte; a test checks). */
export function desktopSealedFrameDocument() {
    return sealedFrameDocument({ parents: [DESKTOP_SHELL_ORIGIN], sameOriginParent: false });
}
//# sourceMappingURL=sealedFrame.js.map